<template>
  <div
    :id="`${source}_custom_selector`"
    :class="[
      'flex gap-2 text_color proxima_nova font-medium',
      computeDirection,
    ]"
  >
    <div
      v-for="(option, index) in options"
      :key="index"
      @click="handleOptionClick(option.id)"
      :class="['selector_item w-full', { selected: selectedId === option.id }]"
    >
      <div class="flex flex-row justify-between relative w-full">
        <div
          :class="[
            'flex flex-row gap-3',
            horizontalAlign,
            computeInnerDirection,
          ]"
        >
          <ThemeImage
            :icon="option.icon"
            :alt="`${option.id}_icon`"
            :style="{
              width: `${option?.size || 32}px`,
              height: `${option?.size || 32}px`,
            }"
          />

          <div
            :class="[
              'flex flex-col gap-[2px]',
              props.directionHorizontal === 'col'
                ? 'items-center justify-center px-2'
                : '',
            ]"
          >
            <span
              :class="[
                props.directionHorizontal === 'col'
                  ? 'text-base font-semibold text_color'
                  : 'text-sm font-semibold text_color',
              ]"
              >{{ option.title }}</span
            >
            <span class="text-xs secondary_color">{{
              option.description
            }}</span>
          </div>
        </div>

        <ThemeImage
          v-if="selectedId === option.id"
          :icon="imageNames.TICK_GREY"
          class="size-4 absolute right-1 top-0"
          alt="tick_icon"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import {
  customSelectorDirections,
  customSelectorHorizontalAlignOptions,
} from "@/src/constants/customComponentConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  options: { type: Object, required: true },
  modelValue: { type: [String, Number], default: null },
  direction: {
    type: String,
    default: customSelectorDirections.VERTICAL,
    validator: (value) =>
      Object.values(customSelectorDirections).includes(value),
  },
  horizontalAlign: {
    type: String,
    default: customSelectorHorizontalAlignOptions.CENTER,
    validator: (value) =>
      Object.values(customSelectorHorizontalAlignOptions).includes(value),
  },
  directionHorizontal: {
    type: String,
    default: "row",
    required: false,
    validator: (value) => ["row", "col"].includes(value),
  },
});

const emit = defineEmits(["update:modelValue"]);

const selectedId = ref(props.modelValue);

const computeDirection = computed(() => {
  if (props.direction === customSelectorDirections.VERTICAL) {
    return "flex-col";
  } else if (props.direction === customSelectorDirections.HORIZONTAL) {
    return "flex-row";
  }
});

const computeInnerDirection = computed(() => {
  return props.directionHorizontal === "row" ? "flex-row" : "flex-col";
});

const handleOptionClick = (id) => {
  selectedId.value = id;
  emit("update:modelValue", id);
};

watch(
  () => props.modelValue,
  (newValue) => {
    selectedId.value = newValue;
  },
);
</script>

<style scoped>
.selector_item {
  display: flex;
  padding: 12px;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid var(--white-opacity-8);
  background: var(--white-opacity-2);
  cursor: pointer;
}

.selector_item.selected {
  border: 1px solid var(--white-opacity-60);
  background: var(--white-opacity-8);
}
</style>
